export function hasAccess(userInfo, allowedLevels) {
  if (!userInfo || !userInfo.level) return false;
  return allowedLevels.includes(userInfo.level);
}

// 사용자 언어 설정 가져오기
export function getUserLanguage(userInfo) {
  if (userInfo && userInfo.preferred_language) {
    return userInfo.preferred_language;
  }
  // 기본값은 한국어
  return 'ko';
}

// 언어 설정 유효성 검사
export function isValidLanguage(language) {
  return ['ko', 'en', 'fr'].includes(language);
}

// 언어 표시명 가져오기
export function getLanguageDisplayName(language) {
  const languageNames = {
    ko: '한국어',
    en: 'English',
    fr: 'Français'
  };
  return languageNames[language] || language;
}

let volatileToken = null;
let csrfTokenRequest = null;

function normalizeToken(token) {
  if (typeof token !== 'string') {
    return null;
  }

  const trimmedToken = token.trim();
  if (!trimmedToken || trimmedToken === 'undefined' || trimmedToken === 'null') {
    return null;
  }

  return trimmedToken;
}

function clearLegacyStoredToken() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem('token');
  } catch (error) {
    // ignore storage access issues
  }

  try {
    window.sessionStorage.removeItem('token');
  } catch (error) {
    // ignore storage access issues
  }
}

// 토큰 관리 함수들 - 브라우저 저장소에는 남기지 않고 메모리에만 유지
export const getToken = () => {
  return volatileToken;
};

export const saveToken = (token) => {
  volatileToken = normalizeToken(token);
  clearLegacyStoredToken();
};

export const getStoredUserInfo = () => {
  const rawUserInfo = localStorage.getItem('userInfo') || sessionStorage.getItem('userInfo');

  if (!rawUserInfo) {
    return null;
  }

  try {
    return JSON.parse(rawUserInfo);
  } catch (error) {
    return null;
  }
};

export const saveUserInfo = (userInfo) => {
  if (!userInfo) {
    clearStoredUserInfo();
    return;
  }

  const serializedUserInfo = JSON.stringify(userInfo);
  localStorage.setItem('userInfo', serializedUserInfo);
  sessionStorage.setItem('userInfo', serializedUserInfo);
};

export const clearToken = () => {
  volatileToken = null;
  clearLegacyStoredToken();
  // Legacy non-HttpOnly token cookie cleanup for older clients
  deleteCookie('token');
};

export const clearStoredUserInfo = () => {
  localStorage.removeItem('userInfo');
  sessionStorage.removeItem('userInfo');
  deleteCookie('userInfo');
};

export const clearAllAuthData = () => {
  clearToken();
  clearStoredUserInfo();

  try {
    localStorage.removeItem('csrfToken');
  } catch (error) {
    // ignore storage access issues
  }
};

export const buildAuthHeaders = (headers = {}) => {
  const token = getToken();

  if (!token) {
    return { ...headers };
  }

  return {
    ...headers,
    Authorization: `Bearer ${token}`
  };
};

// 쿠키 관리 함수들 (iOS PWA 토큰 복구용)
export const setCookie = (name, value, days) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
};

export const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

export const deleteCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

// Legacy API retained for compatibility; token cookies are no longer used.
export const recoverTokenFromCookie = () => {
  return null;
};

const decodeJwtPayload = (token) => {
  if (!token) {
    return null;
  }

  const [, payloadPart] = token.split('.');
  if (!payloadPart) {
    return null;
  }

  try {
    const normalizedPayload = payloadPart
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const paddedPayload = normalizedPayload.padEnd(
      Math.ceil(normalizedPayload.length / 4) * 4,
      '='
    );

    return JSON.parse(atob(paddedPayload));
  } catch (error) {
    return null;
  }
};

// ===== CSRF 토큰 관리 =====

// CSRF 토큰 가져오기 (저장소에서)
export const getCsrfToken = () => {
  return localStorage.getItem('csrfToken');
};

// CSRF 토큰 저장
export const saveCsrfToken = (token) => {
  if (token) {
    localStorage.setItem('csrfToken', token);
  }
};

// CSRF 토큰 발급 API 호출
export const fetchCsrfToken = async () => {
  try {
    const response = await fetch('/api/csrf-token', {
      credentials: 'include'
    });
    if (response.ok) {
      const data = await response.json();
      if (data.csrfToken) {
        saveCsrfToken(data.csrfToken);
        return data.csrfToken;
      }
    }
    return null;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('CSRF 토큰 발급 실패:', error);
    }
    return null;
  }
};

export const ensureCsrfToken = async ({ forceRefresh = false } = {}) => {
  if (!forceRefresh) {
    const existingToken = getCsrfToken();
    if (existingToken) {
      return existingToken;
    }
  }

  if (!csrfTokenRequest || forceRefresh) {
    const request = fetchCsrfToken();
    csrfTokenRequest = request.finally(() => {
      if (csrfTokenRequest === request) {
        csrfTokenRequest = null;
      }
    });
  }

  return csrfTokenRequest;
};

const CSRF_ERROR_PATTERN = /CSRF token missing|Invalid CSRF token/i;

const isCsrfErrorResponse = async (response) => {
  if (!response || response.status !== 403) {
    return false;
  }

  try {
    const clonedResponse = response.clone();
    const data = await clonedResponse.json();
    const message = data?.error || data?.message || '';
    return CSRF_ERROR_PATTERN.test(message);
  } catch (jsonError) {
    try {
      const clonedResponse = response.clone();
      const text = await clonedResponse.text();
      return CSRF_ERROR_PATTERN.test(text);
    } catch (textError) {
      return false;
    }
  }
};

// 상태 변경 메소드인지 확인 (CSRF 필요)
const isStateChangingMethod = (method) => {
  const upperMethod = (method || 'GET').toUpperCase();
  return ['POST', 'PUT', 'DELETE', 'PATCH'].includes(upperMethod);
};

// 401 에러 처리 및 자동 로그아웃 함수
export const handleAuthError = (response) => {
  if (response && response.status === 401) {
    // 이미 로그아웃 처리 중인지 확인
    if (window._logoutInProgress) {
      return true;
    }

    // 로그아웃 처리 중 표시
    window._logoutInProgress = true;

    clearAllAuthData();

    // 강제 페이지 이동 제거 - React Router가 처리하도록 함
    // UserContext에서 userInfo가 null이 되면 ProtectedRoute가 자동으로 /login으로 리다이렉트

    // 로그아웃 처리 완료 표시 제거 (일정 시간 후)
    setTimeout(() => {
      window._logoutInProgress = false;
    }, 2000);

    return true; // 에러 처리됨
  }
  return false; // 401 에러가 아님
};

// API 호출 래퍼 함수 (401 에러 자동 처리 + CSRF 보호)
export const apiCall = async (url, options = {}) => {
  try {
    options.headers = buildAuthHeaders(options.headers);

    // 상태 변경 요청에 CSRF 토큰 추가
    if (isStateChangingMethod(options.method)) {
      const csrfToken = await ensureCsrfToken();
      if (csrfToken) {
        options.headers = {
          ...options.headers,
          'X-CSRF-Token': csrfToken
        };
      }
    }

    // credentials 항상 포함
    options.credentials = 'include';

    const response = await fetch(url, options);

    // 401 에러 자동 처리
    if (response.status === 401) {
      handleAuthError(response);
      return null;
    }

    return response;
  } catch (error) {
    // 네트워크 오류는 개발 환경에서만 로그 출력
    if (process.env.NODE_ENV === 'development') {
      console.error('API 호출 오류:', error);
    }
    return null;
  }
};

// JSON 응답을 자동으로 파싱하는 API 호출 함수
export const apiCallJson = async (url, options = {}) => {
  const response = await apiCall(url, options);
  if (!response) return null;

  try {
    return await response.json();
  } catch (error) {
    console.error('JSON 파싱 오류:', error);
    return null;
  }
};

// 토큰 만료 체크 및 자동 로그아웃 함수
export const checkTokenAndLogout = () => {
  const token = getToken();

  if (!token) {
    return false;
  }

  if (isTokenExpired()) {
    handleAuthError({ status: 401 });
    return true;
  }

  return false;
};

// 토큰 만료 체크 함수
export const isTokenExpired = () => {
  const token = getToken();
  if (!token) return false;

  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') {
    return false;
  }

  const currentTime = Date.now() / 1000;
  return payload.exp < currentTime;
};

export const fetchCurrentUser = async () => {
  try {
    const headers = buildAuthHeaders({
      'Content-Type': 'application/json'
    });

    const response = await fetch('/api/me', {
      method: 'GET',
      headers,
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      return {
        ok: true,
        status: response.status,
        user: data.user || data,
      };
    }

    if (response.status === 401) {
      handleAuthError(response);
    }

    return {
      ok: false,
      status: response.status,
      user: null,
    };
  } catch (error) {
    return {
      ok: false,
      status: 0,
      user: null,
      error,
    };
  }
};

// 토큰 유효성 검증 함수
export const validateToken = async () => {
  const result = await fetchCurrentUser();
  return result.ok;
};

// 전역 fetch 인터셉터 설정 (401 에러 자동 처리 + CSRF 토큰 자동 추가)
export const setupGlobalFetchInterceptor = () => {
  if (window._fetchInterceptorSet) {
    return;
  }

  const originalFetch = window.fetch;

  window.fetch = async function (input, init = {}) {
    try {
      // URL 추출
      const url = typeof input === 'string' ? input : input.url;
      const method = (init.method || (typeof input === 'string' ? 'GET' : input.method) || 'GET').toUpperCase();
      const isApiRequest = Boolean(url && url.startsWith('/api'));
      const isStateChangingApiRequest = isApiRequest && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method);
      const requestInit = { ...init };

      // API 요청인 경우에만 CSRF 토큰 추가
      if (isStateChangingApiRequest) {
        const csrfToken = await ensureCsrfToken();
        if (csrfToken) {
          requestInit.headers = {
            ...(requestInit.headers || {}),
            'X-CSRF-Token': csrfToken
          };
        }
      }

      let response = await originalFetch.call(this, input, requestInit);

      if (
        isStateChangingApiRequest &&
        typeof input === 'string' &&
        !requestInit.__csrfRetried &&
        await isCsrfErrorResponse(response)
      ) {
        const refreshedCsrfToken = await ensureCsrfToken({ forceRefresh: true });
        if (refreshedCsrfToken) {
          response = await originalFetch.call(this, input, {
            ...requestInit,
            __csrfRetried: true,
            headers: {
              ...(requestInit.headers || {}),
              'X-CSRF-Token': refreshedCsrfToken
            }
          });
        }
      }

      if (response.status === 401) {
        handleAuthError(response);
        return response.clone();
      }

      return response;
    } catch (error) {
      if (error.name === 'AbortError') {
        return new Promise(() => { });
      }
      return Promise.reject(error);
    }
  };

  window._fetchInterceptorSet = true;
};

// 전역 토큰 감지 시스템 설정
export const setupGlobalTokenMonitor = () => {
  if (window._tokenMonitorSet) {
    return;
  }

  const globalTokenCheck = () => {
    const token = getToken();

    if (!token) {
      return;
    }

    if (isTokenExpired()) {
      handleAuthError({ status: 401 });
      return;
    }
  };

  const tokenCheckInterval = setInterval(globalTokenCheck, 15000);

  const handleFocus = () => {
    globalTokenCheck();
  };

  const handleVisibilityChange = () => {
    if (!document.hidden) {
      globalTokenCheck();
    }
  };

  window.addEventListener('focus', handleFocus);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  window._tokenMonitorSet = true;

  return () => {
    clearInterval(tokenCheckInterval);
    window.removeEventListener('focus', handleFocus);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
};

// 앱 시작 시 인터셉터 설정 (즉시 실행)
if (typeof window !== 'undefined') {
  clearLegacyStoredToken();
  setupGlobalFetchInterceptor();
}
